import { ParameterId as Api, VerbId as ApiVerbId } from '@tableau/api-internal-contract-js';
import {
  DocCommands as PresLayerDocCommandId,
  SrvCommands as PresLayerSrvCommandId,
  UICommands as PresLayerUICommandId,
} from '../../gen-src/preslayer/CommandIds';
import { ApiShowDataTableIdSentinel, FiltersQuantitativeIncludedValues } from '../../gen-src/preslayer/Enums';
import { ParameterId as PL } from '../../gen-src/preslayer/Params';
import { ApiContext } from '../ApiContext';
import { CommandMappingRegistry } from './CommandMappingRegistry';
import { CommandNamespace, CommandParameter, CommandRegistration } from './CommandRegistration';
import { makeEmptyDataTableReader } from './DataTableReaderConverter';
import { makeEmptyExportFile } from './ExportConverter';
import { DataScope, makeEmptyDataTable } from './ShowDataConverter';
import { VersionedCommandRegistration } from './VersionedCommandRegistration';

const SHOW_DATA_COMMAND_VERSION_2_DOT_0 = '2.0';

export class CommandMappingRegistryFactory {
  /**
   * Creates and populates a new CommandMappingRegistry with commands common to both Extensions and Embedding
   *
   * @static
   * @returns {CommandMappingRegistry}
   * @memberof CommandMappingRegistryFactory
   */
  public static createCommandMappingRegistry(): CommandMappingRegistry {
    const result = new CommandMappingRegistry();
    const apiContext = ApiContext.getInstance();

    result.addCommand(
      new VersionedCommandRegistration(
        SHOW_DATA_COMMAND_VERSION_2_DOT_0,
        ApiVerbId.GetUnderlyingData,
        PresLayerDocCommandId.ApiGetWorksheetUnderlyingLogicalTableData,
        [
          new CommandParameter(Api.IgnoreAliases, PL.IgnoreAliases),
          new CommandParameter(Api.IgnoreSelection, PL.IgnoreSelection),
          new CommandParameter(Api.IncludeAllColumns, PL.IncludeAllColumns),
          CommandParameter.makeOptional(Api.ApplyWorksheetFormatting, PL.ApplyFormatting),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
          new CommandParameter(Api.MaxRows, PL.MaxRows),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
          CommandParameter.makeWithDefaultValue(Api.LogicalTableId, PL.ShowDataTableId, ApiShowDataTableIdSentinel.SingleTableIdSentinel),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
        ],
        [CommandParameter.makeWithDefaultValue(Api.UnderlyingDataTable, PL.DataTablePresModel, makeEmptyDataTable(DataScope.Underlying))],
      ),
    );

    result.addCommand(
      new VersionedCommandRegistration(
        SHOW_DATA_COMMAND_VERSION_2_DOT_0,
        ApiVerbId.GetUnderlyingTableData,
        PresLayerDocCommandId.ApiGetWorksheetUnderlyingLogicalTableData,
        [
          new CommandParameter(Api.IgnoreAliases, PL.IgnoreAliases),
          new CommandParameter(Api.IgnoreSelection, PL.IgnoreSelection),
          new CommandParameter(Api.IncludeAllColumns, PL.IncludeAllColumns),
          CommandParameter.makeOptional(Api.ApplyWorksheetFormatting, PL.ApplyFormatting),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
          new CommandParameter(Api.LogicalTableId, PL.ShowDataTableId),
          new CommandParameter(Api.MaxRows, PL.MaxRows),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
        ],
        [CommandParameter.makeWithDefaultValue(Api.UnderlyingDataTable, PL.DataTablePresModel, makeEmptyDataTable(DataScope.Underlying))],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetUnderlyingTableDataReader,
        PresLayerDocCommandId.CreateUnderlyingDataTableReader,
        [
          new CommandParameter(Api.ViewDataTableCacheId, PL.UUID),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
          new CommandParameter(Api.LogicalTableId, PL.ShowDataTableId),
          new CommandParameter(Api.PageRowCount, PL.URowCount),
          CommandParameter.makeOptional(Api.IgnoreAliases, PL.IgnoreAliases),
          CommandParameter.makeOptional(Api.IgnoreSelection, PL.IgnoreSelection),
          CommandParameter.makeOptional(Api.ApplyWorksheetFormatting, PL.ApplyFormatting),
          CommandParameter.makeOptional(Api.IncludeAllColumns, PL.IncludeAllColumns),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
        ],
        [CommandParameter.makeWithDefaultValue(Api.DataTableReader, PL.DataTableReader, makeEmptyDataTableReader())],
      ),
    );

    result.addCommand(
      new VersionedCommandRegistration(
        SHOW_DATA_COMMAND_VERSION_2_DOT_0,
        ApiVerbId.GetDataSummaryData,
        PresLayerDocCommandId.ApiGetWorksheetSummaryLogicalTableData,
        [
          new CommandParameter(Api.IgnoreAliases, PL.IgnoreAliases),
          new CommandParameter(Api.IgnoreSelection, PL.IgnoreSelection),
          CommandParameter.makeOptional(Api.ApplyWorksheetFormatting, PL.ApplyFormatting),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
          CommandParameter.makeOptional(Api.MaxRows, PL.MaxRows),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
        ],
        [
          CommandParameter.makeWithDefaultValue(
            Api.UnderlyingSummaryDataTable,
            PL.DataTablePresModel,
            makeEmptyDataTable(DataScope.Summary),
          ),
        ],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetDataSummaryDataReader,
        PresLayerDocCommandId.CreateSummaryDataTableReader,
        [
          new CommandParameter(Api.ViewDataTableCacheId, PL.UUID),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
          new CommandParameter(Api.PageRowCount, PL.URowCount),
          CommandParameter.makeOptional(Api.IgnoreAliases, PL.IgnoreAliases),
          CommandParameter.makeOptional(Api.IgnoreSelection, PL.IgnoreSelection),
          CommandParameter.makeOptional(Api.ApplyWorksheetFormatting, PL.ApplyFormatting),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
        ],
        [CommandParameter.makeWithDefaultValue(Api.DataTableReader, PL.DataTableReader, makeEmptyDataTableReader())],
      ),
    );

    result.addCommand(
      new VersionedCommandRegistration(
        SHOW_DATA_COMMAND_VERSION_2_DOT_0,
        ApiVerbId.GetLogicalTableData,
        PresLayerDocCommandId.ApiGetDatasourceLogicalTableData,
        [
          new CommandParameter(Api.DataSourceId, PL.Datasource),
          new CommandParameter(Api.IgnoreAliases, PL.IgnoreAliases),
          new CommandParameter(Api.LogicalTableId, PL.ShowDataTableId),
          new CommandParameter(Api.MaxRows, PL.MaxRows),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
          new CommandParameter(Api.ColumnsToInclude, PL.FieldCaptions),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
        ],
        [CommandParameter.makeWithDefaultValue(Api.UnderlyingDataTable, PL.DataTablePresModel, makeEmptyDataTable(DataScope.Underlying))],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetLogicalTableDataReader,
        PresLayerDocCommandId.CreateLogicalDataTableReader,
        [
          new CommandParameter(Api.ViewDataTableCacheId, PL.UUID),
          new CommandParameter(Api.DataSourceId, PL.Datasource),
          new CommandParameter(Api.LogicalTableId, PL.ShowDataTableId),
          new CommandParameter(Api.PageRowCount, PL.URowCount),
          CommandParameter.makeOptional(Api.IgnoreAliases, PL.IgnoreAliases),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
        ],
        [CommandParameter.makeWithDefaultValue(Api.DataTableReader, PL.DataTableReader, makeEmptyDataTableReader())],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetDataTableReaderPage,
        PresLayerDocCommandId.GetDataTableReaderPage,
        [
          new CommandParameter(Api.ViewDataTableCacheId, PL.UUID),
          new CommandParameter(Api.ViewDataTableId, PL.ViewDataTableId),
          new CommandParameter(Api.DataRowIndex, PL.RowIndex),
        ],
        [new CommandParameter(Api.DataTable, PL.DataTableReaderPage)],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.ReleaseDataTableReader, PresLayerDocCommandId.ReleaseDataTableReader, [
        new CommandParameter(Api.ViewDataTableCacheId, PL.UUID),
        new CommandParameter(Api.ViewDataTableId, PL.ViewDataTableId),
      ]),
    );

    result.addCommand(
      new VersionedCommandRegistration(
        SHOW_DATA_COMMAND_VERSION_2_DOT_0,
        ApiVerbId.GetDataSourceData,
        PresLayerDocCommandId.ApiGetDatasourceLogicalTableData,
        [
          new CommandParameter(Api.DataSourceId, PL.Datasource),
          new CommandParameter(Api.IgnoreAliases, PL.IgnoreAliases),
          new CommandParameter(Api.MaxRows, PL.MaxRows),
          CommandParameter.makeOptional(Api.ShowDataTableFormat, PL.ShowDataTableFormat),
          CommandParameter.makeWithDefaultValue(Api.LogicalTableId, PL.ShowDataTableId, ApiShowDataTableIdSentinel.SingleTableIdSentinel),
          new CommandParameter(Api.ColumnsToInclude, PL.FieldCaptions),
          CommandParameter.makeOptional(Api.ColumnsToIncludeById, PL.ColumnFullNames),
        ],
        [CommandParameter.makeWithDefaultValue(Api.UnderlyingDataTable, PL.DataTablePresModel, makeEmptyDataTable(DataScope.Underlying))],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ApplyCategoricalFilter,
        PresLayerDocCommandId.CategoricalFilter,
        [
          new CommandParameter(Api.FieldName, PL.QualifiedFieldCaption),
          new CommandParameter(Api.FilterValues, PL.FilterAliases),
          new CommandParameter(Api.FilterUpdateType, PL.FilterUpdateType),
          CommandParameter.makeOptional(Api.IsExcludeMode, PL.Exclude),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
        ],
        [new CommandParameter(Api.FieldName, PL.FieldCaption)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ApplyRangeFilter,
        PresLayerDocCommandId.RangeFilter,
        [
          CommandParameter.makeOptional(Api.FieldName, PL.QualifiedFieldCaption),
          CommandParameter.makeOptional(Api.FilterRangeMin, PL.RangeMin),
          CommandParameter.makeOptional(Api.FilterRangeMax, PL.RangeMax),
          CommandParameter.makeWithDefaultValue(Api.FilterRangeNullOption, PL.Included, FiltersQuantitativeIncludedValues.InRangeOrNull),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
        ],
        [
          CommandParameter.makeOptional(Api.FieldName, PL.FieldCaption),
          CommandParameter.makeOptional(Api.ParameterError, PL.ParameterError),
          CommandParameter.makeOptional(Api.InvalidFieldCaption, PL.InvalidFieldCaption),
          CommandParameter.makeOptional(Api.InvalidAggFieldName, PL.InvalidAggFieldName),
          CommandParameter.makeOptional(Api.InvalidValues, PL.InvalidValues),
        ],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ClearFilter,
        PresLayerDocCommandId.ResetFilter,
        [new CommandParameter(Api.FieldName, PL.QualifiedFieldCaption), new CommandParameter(Api.VisualId, PL.VisualIDPM)],
        [new CommandParameter(Api.FieldName, PL.FieldCaption)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ApplyRelativeDateFilter,
        PresLayerDocCommandId.RelDateFilter,
        [
          new CommandParameter(Api.FieldName, PL.QualifiedFieldCaption),
          CommandParameter.makeOptional(Api.AnchorDate, PL.AnchorDate),
          new CommandParameter(Api.PeriodType, PL.DatePeriodType),
          new CommandParameter(Api.DateRangeType, PL.DateRangeType),
          CommandParameter.makeOptional(Api.RangeN, PL.RangeN), // Required at certain conditions. Bail out early when that happens. That makes it optional
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
        ],
        [new CommandParameter(Api.FieldName, PL.FieldCaption)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetSelectedMarks,
        PresLayerDocCommandId.GetSelectionData,
        [new CommandParameter(Api.VisualId, PL.VisualIDPM)],
        [new CommandParameter(Api.SelectedData, PL.SelectionData)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.FindParameter,
        PresLayerDocCommandId.FindParameterModel,
        [
          CommandParameter.makeOptional(Api.ParameterCaption, PL.FieldCaption),
          CommandParameter.makeOptional(Api.ParameterFieldName, PL.FieldNameString),
        ],
        // We might not get a response if the parameter isn't found
        [CommandParameter.makeOptional(Api.ParameterInfo, PL.ParameterCtrl)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetParametersForSheet,
        PresLayerDocCommandId.GetParameterModels,
        [],
        [new CommandParameter(Api.ParameterInfos, PL.ParameterCtrls)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ChangeParameterValue,
        PresLayerDocCommandId.SetParameterValue,
        [new CommandParameter(Api.ParameterFieldName, PL.GlobalFieldName), new CommandParameter(Api.ParameterValue, PL.ValueStr)],
        [new CommandParameter(Api.ParameterInfo, PL.ParameterCtrl)],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.ClearSelectedMarks, PresLayerDocCommandId.ClearSelectedMarks, [
        new CommandParameter(Api.VisualId, PL.VisualIDPM),
      ]),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SelectByValue,
        PresLayerDocCommandId.SelectByValue,
        [
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
          CommandParameter.makeOptional(Api.Selection, PL.Selection),
          new CommandParameter(Api.SelectionUpdateType, PL.SelectionUpdateType),
          CommandParameter.makeOptional(Api.HierValSelectionModels, PL.HierValSelectionModels),
          CommandParameter.makeOptional(Api.DimValSelectionModels, PL.DimValSelectionModels),
          CommandParameter.makeOptional(Api.QuantRangeSelectionModels, PL.QuantRangeSelectionModels),
        ],
        [
          CommandParameter.makeOptional(Api.ParameterError, PL.ParameterError),
          CommandParameter.makeOptional(Api.InvalidFields, PL.InvalidFields),
          CommandParameter.makeOptional(Api.InvalidDates, PL.InvalidDates),
          CommandParameter.makeOptional(Api.InvalidValues, PL.InvalidValues),
        ],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetDataSources,
        PresLayerDocCommandId.ApiGetDataSchemaPresModel,
        [new CommandParameter(Api.VisualId, PL.Worksheet)],
        [new CommandParameter(Api.DataSchema, PL.DataSchema)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.RefreshDataSource,
        apiContext.isDesktop() ? PresLayerUICommandId.RefreshDataSourceUI : PresLayerSrvCommandId.RefreshDataServer,
        apiContext.isDesktop()
          ? [new CommandParameter(Api.DataSourceId, PL.Datasource)]
          : [
              CommandParameter.makeOptional(Api.DataSourceId, PL.DatasourceId),
              new CommandParameter(Api.DeltaTimeMs, PL.DeltaTimeMs),
              new CommandParameter(Api.ShouldRefreshDS, PL.ShouldRefreshDS),
            ],
        undefined,
        apiContext.isDesktop() ? CommandNamespace.TabUI : CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetFilters,
        PresLayerDocCommandId.GetWorksheetFilters,
        [new CommandParameter(Api.VisualId, PL.VisualIDPM)],
        [new CommandParameter(Api.WorksheetFilters, PL.FiltersInfo)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetCategoricalDomain,
        PresLayerDocCommandId.GetCategoricalDomain,
        [
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
          new CommandParameter(Api.FieldId, PL.FieldName),
          new CommandParameter(Api.DomainType, PL.FilterDomainType),
        ],
        [new CommandParameter(Api.CategoricalDomain, PL.CategoricalDomain)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetRangeDomain,
        PresLayerDocCommandId.GetQuantitativeDomain,
        [
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
          new CommandParameter(Api.FieldId, PL.FieldName),
          new CommandParameter(Api.DomainType, PL.FilterDomainType),
        ],
        [new CommandParameter(Api.QuantitativeDomain, PL.QuantitativeDomain)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetJoinDescription,
        PresLayerDocCommandId.GetJoinDescriptionPresModel,
        [new CommandParameter(Api.DataSourceId, PL.Datasource)],
        [new CommandParameter(Api.JoinDescription, PL.JoinDescription)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetActiveTables,
        PresLayerDocCommandId.GetActiveTables,
        [new CommandParameter(Api.DataSourceId, PL.Datasource)],
        [new CommandParameter(Api.ActiveTablesInfo, PL.ActiveTableInfos)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetDataSource,
        PresLayerDocCommandId.GetDataSourcePresModel,
        [new CommandParameter(Api.DataSourceId, PL.Datasource)],
        [new CommandParameter(Api.DataSource, PL.DataSource)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetConnectionDescriptionSummaries,
        PresLayerDocCommandId.GetConnectionDescriptionSummaryPresModels,
        [new CommandParameter(Api.DataSourceId, PL.Datasource)],
        [new CommandParameter(Api.ConnectionDescriptionSummaries, PL.ConnectionDescriptionSummaries)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetFieldAndDataSource,
        PresLayerDocCommandId.GetFieldAndDatasource,
        [new CommandParameter(Api.FieldId, PL.FieldName)],
        [new CommandParameter(Api.Field, PL.Field), new CommandParameter(Api.DataSource, PL.DataSource)],
      ),
    );

    result.addCommand(
      new VersionedCommandRegistration(
        SHOW_DATA_COMMAND_VERSION_2_DOT_0,
        ApiVerbId.GetLogicalTables,
        PresLayerDocCommandId.ApiGetDatasourceLogicalTableIds,
        [new CommandParameter(Api.DataSourceId, PL.Datasource)],
        [new CommandParameter(Api.LogicalTableInfoList, PL.ShowDataTableIdWithCaptionPresModelList)],
      ),
    );

    result.addCommand(
      new VersionedCommandRegistration(
        SHOW_DATA_COMMAND_VERSION_2_DOT_0,
        ApiVerbId.GetUnderlyingTables,
        PresLayerDocCommandId.ApiGetWorksheetLogicalTableIds,
        [new CommandParameter(Api.VisualId, PL.VisualIDPM)],
        [new CommandParameter(Api.LogicalTableInfoList, PL.ShowDataTableIdWithCaptionPresModelList)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetDashboardFilters,
        PresLayerDocCommandId.GetDashboardFilters,
        [],
        [new CommandParameter(Api.DashboardFilters, PL.FiltersInfo)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.DashboardCategoricalFilter,
        PresLayerDocCommandId.DashboardCategoricalFilter,
        [
          new CommandParameter(Api.FieldName, PL.QualifiedFieldCaption),
          new CommandParameter(Api.FilterValues, PL.FilterAliases),
          new CommandParameter(Api.FilterUpdateType, PL.FilterUpdateType),
          CommandParameter.makeOptional(Api.IsExcludeMode, PL.Exclude),
        ],
        [new CommandParameter(Api.FieldName, PL.FieldCaption)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetSharedFilter,
        PresLayerDocCommandId.GetSharedFilter,
        [new CommandParameter(Api.VisualId, PL.Worksheet), new CommandParameter(Api.FieldId, PL.FieldName)],
        [new CommandParameter(Api.SharedFiltersInfo, PL.SharedFilterPresModel)],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.ChangeSharedFilter, PresLayerDocCommandId.ChangeSharedFilter, [
        new CommandParameter(Api.VisualId, PL.Worksheet),
        new CommandParameter(Api.FieldId, PL.FieldName),
        new CommandParameter(Api.SharedFilterSheets, PL.Sheets),
      ]),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.HierarchicalFilter,
        PresLayerDocCommandId.HierarchicalFilter,
        [
          new CommandParameter(Api.FieldName, PL.QualifiedFieldCaption),
          CommandParameter.makeOptional(Api.FilterValues, PL.FilterAliases),
          new CommandParameter(Api.FilterUpdateType, PL.FilterUpdateType),
          CommandParameter.makeOptional(Api.IsExcludeMode, PL.Exclude),
          CommandParameter.makeOptional(Api.FilterLevels, PL.FilterLevels),
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
        ],
        [new CommandParameter(Api.FieldName, PL.FieldCaption)],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.CreateAnnotation, PresLayerDocCommandId.CreateAnnotation, [
        new CommandParameter(Api.VisualId, PL.VisualIDPM),
        CommandParameter.makeOptional(Api.SelectionList, PL.SelectionList),
        new CommandParameter(Api.AnnotateEnum, PL.AnnotateEnum),
        new CommandParameter(Api.TargetPoint, PL.TargetPt),
        CommandParameter.makeOptional(Api.Text, PL.FormattedText),
      ]),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetAnnotations,
        PresLayerDocCommandId.GetAnnotations,
        [new CommandParameter(Api.VisualId, PL.VisualIDPM)],
        [new CommandParameter(Api.AnnotationList, PL.AnnotationList)],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.RemoveAnnotation, PresLayerDocCommandId.RemoveAnnotation, [
        new CommandParameter(Api.VisualId, PL.VisualIDPM),
        CommandParameter.makeOptional(Api.SelectionList, PL.SelectionList),
      ]),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ExportCrosstabCsvDownload,
        PresLayerSrvCommandId.ExportCrosstabToCSVServer,
        [
          new CommandParameter(Api.SheetIdentifier, PL.SheetSimpleID),
          new CommandParameter(Api.SendNotifications, PL.SendNotifications),
          new CommandParameter(Api.UseTabDelimiters, PL.UseTabs),
        ],
        // We might not get a response if we send notifications
        [CommandParameter.makeWithDefaultValue(Api.ExportFile, PL.ExportFilePresModel, makeEmptyExportFile())],
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ExportCrosstabExcelDownload,
        PresLayerSrvCommandId.ExportCrosstabToExcelServer,
        [new CommandParameter(Api.SheetIdentifier, PL.SheetSimpleID), new CommandParameter(Api.SendNotifications, PL.SendNotifications)],
        // We might not get a response if we send notifications
        [CommandParameter.makeWithDefaultValue(Api.ExportFile, PL.ExportFilePresModel, makeEmptyExportFile())],
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetExportCrosstabSheetMap,
        PresLayerDocCommandId.GetExportCrosstabSheetOptions,
        [new CommandParameter(Api.CurrentSheetType, PL.SheetType)],
        [new CommandParameter(Api.ExportCrosstabSheetMap, PL.ExportCrosstabOptionsDialogPresModel)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ExportDataDownload,
        PresLayerSrvCommandId.ExportViewDataSummaryToCsvServer,
        [
          new CommandParameter(Api.VisualId, PL.VisualIDPM),
          new CommandParameter(Api.IgnoreAliases, PL.ShowAliases),
          new CommandParameter(Api.ColumnsToIncludeById, PL.Columns),
        ],
        [],
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ExportPowerpointDownload,
        PresLayerSrvCommandId.PowerPointExportServer,
        [new CommandParameter(Api.ExportOriginUrl, PL.ExportOriginURL), new CommandParameter(Api.SelectedSheetNames, PL.SelectedSheetIDs)],
        [new CommandParameter(Api.ExportFile, PL.ExportFilePresModel)],
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ExportPdfDownload,
        PresLayerSrvCommandId.ExportPdfServer,
        [new CommandParameter(Api.ExportPdfOptions, PL.ExportPdfOptionsPresModel)],
        [new CommandParameter(Api.ExportFile, PL.ExportFilePresModel)],
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetExportPdfOptions,
        PresLayerSrvCommandId.GetExportPdfOptions,
        [],
        [new CommandParameter(Api.ExportPdfOptions, PL.ExportPdfOptionsPresModel)],
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetVisualSpecification,
        PresLayerDocCommandId.GetVisualSpecification,
        [new CommandParameter(Api.WorksheetName, PL.Worksheet)],
        [new CommandParameter(Api.VisualSpecification, PL.VisualSpecification)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.AddMarksCardFields,
        PresLayerDocCommandId.AddMarksCardFields,
        [
          new CommandParameter(Api.WorksheetName, PL.Worksheet),
          new CommandParameter(Api.MarksCardIndex, PL.MarksCardIndex),
          new CommandParameter(Api.EncodingType, PL.EncodingType),
          new CommandParameter(Api.Columns, PL.Columns),
          new CommandParameter(Api.StartIndex, PL.StartIndex),
        ],
        [],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.MoveMarksCardField,
        PresLayerDocCommandId.MoveMarksCardField,
        [
          new CommandParameter(Api.WorksheetName, PL.Worksheet),
          new CommandParameter(Api.MarksCardIndex, PL.MarksCardIndex),
          new CommandParameter(Api.FromIndex, PL.FromIndex),
          new CommandParameter(Api.ToIndex, PL.ToIndex),
          new CommandParameter(Api.FieldCount, PL.FieldCount),
        ],
        [],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SpliceMarksCardFields,
        PresLayerDocCommandId.SpliceMarksCardFields,
        [
          new CommandParameter(Api.WorksheetName, PL.Worksheet),
          new CommandParameter(Api.MarksCardIndex, PL.MarksCardIndex),
          new CommandParameter(Api.EncodingType, PL.EncodingType),
          new CommandParameter(Api.StartIndex, PL.StartIndex),
          new CommandParameter(Api.DeleteCount, PL.DeleteCount),
          new CommandParameter(Api.Columns, PL.Columns),
        ],
        [],
      ),
    );

    return result;
  }
}
